/**
 * What a finished bulk run actually did, per element.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Refusals are the interesting half and they are all DIFFERENT — at capacity,
 * would break the live floor, kill switch, tracker rejected it. A toast can
 * only carry a count, so the reasons live here until dismissed, quoted from the
 * server rather than restated by the client.
 */

import { FC } from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';

import type { BulkItemResult } from '../useBulkRunner';

export interface BulkResultPanelProps {
  results: BulkItemResult[];
  onDismiss: () => void;
}

const BulkResultPanel: FC<BulkResultPanelProps> = ({ results, onDismiss }) => {
  if (results.length === 0) return null;
  const failed = results.filter((r) => !r.ok);
  const okCount = results.length - failed.length;
  if (failed.length === 0) return null;
  return (
    <div
      data-testid="bulk-result-panel"
      className="rounded-md border border-warning/40 bg-warning/[0.07] px-2 py-1.5"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
        <span className="text-xs font-semibold">
          {okCount > 0 ? (
            <>
              <Check className="mr-1 inline h-3 w-3" aria-hidden />
              {okCount} done ·{' '}
            </>
          ) : null}
          {failed.length} not applied
        </span>
        <button
          type="button"
          onClick={onDismiss}
          title="Dismiss"
          className="ml-auto rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <ul className="mt-1 space-y-0.5">
        {failed.map((r) => (
          <li
            key={`${r.element.layer}:${r.element.bankId}`}
            className="flex gap-2 text-[11px] leading-snug"
          >
            <span className="shrink-0 font-medium">
              {r.element.itemName || 'Unnamed'}
            </span>
            <span className="min-w-0 text-muted-foreground">{r.message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BulkResultPanel;
